<template>
  <div>
    <div
      :key="$_sideMenu"
      v-sticky
      sticky-z-index="9"
      sticky-offset="$_offset"
    >
      <v-tabs
        v-model="activeTab"
        :mandatory="false"
        show-arrows
        background-color="grey lighten-4"
        height="36"
        class="mb-0"
      >
        <v-tab value="cap" @click="loadPayments('cap')">Client Payable</v-tab>
        <v-tab value="car" @click="loadPayments('car')">Client Receivable</v-tab>
        <v-tab value="canap" @click="loadPayments('canap')">Cananwill Payable</v-tab>
        <v-tab value="canar" @click="loadPayments('canar')">Cananwill Receivable</v-tab>
        <v-tab value="lpp" @click="loadPayments('lpp')">LPP Statement</v-tab>
        <v-tab value="ap" @click="loadPayments('ap')">Misc. Payable</v-tab>
        <v-tab value="ar" @click="loadPayments('ar')">Misc. Receivable</v-tab>
      </v-tabs>

      <v-card
        v-if="hasLoadedAny"
        :key="`${$_sideMenu}-${activeTab}`"
        class="mb-1 px-3 d-flex justify-space-between align-center payments-summary-bar"
        height="26px"
        tile
        dark
        block
        color="primary"
      >
        <span class="pl-3 payments-summary-text">{{ currentReportLabel }}</span>
        <span class="pr-3 payments-summary-text">Generated: {{ reportDate }}</span>
      </v-card>
    </div>

    <v-card
      v-if="!hasLoadedAny"
      class="d-flex align-center justify-center subtitle-1 grey--text text--darken-1"
      outlined
      height="150"
    >
      Click tab to generate report
    </v-card>

    <div v-else>
      <div class="d-flex justify-end mb-1 pr-2">
        <div style="width: 50vw; max-width: 50vw; padding-right: 4px;">
          <search-input
            v-model="filter"
            class="mt-0"
            dense
            label="Search"
            :hint="searchCountLabel"
            persistent-hint
            hint-rail
            :input-props="{ appendIcon: 'mdi-magnify', singleLine: true, hideDetails: 'auto', clearable: true }"
          />
        </div>
      </div>

      <v-data-table
        :key="`payments-table-${currentReportType}-${showActionsColumn}`"
        :class="['elevation-1 payments-table', { 'no-actions': !showActionsColumn }]"
        dense
        :loading="loading"
        loading-text="Loading... Please wait"
        fixed-header
        disable-pagination
        hide-default-footer
        :headers="displayHeaders"
        :items="filteredRows"
        :mobile-breakpoint="0"
        multi-sort
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :style="$_cssVars"
      >
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center" style="width: 100%; gap: 2px;">
            <button-tip
              v-if="item.pid && canPolicyEdit"
              @click="$_editPolicy(item.pid)"
              icon
              iname="mdi-pencil"
              small
              tip="Edit Policy"
            />
            <button-tip
              v-if="item.fid && canFirmView"
              @click="$_viewFirmNoNotePopup(item.fid)"
              icon
              iname="mdi-open-in-new"
              small
              tip="Open Firm"
            />
          </div>
        </template>

        <template v-slot:item.amountValue="{ item }">
          <span class="text-right d-inline-block" style="width: 100%; padding-right: 8px;">{{ formatCurrency(item.amountValue) }}</span>
        </template>

        <template v-slot:item.inceptSortValue="{ item }">
          <span>{{ item.incept }}</span>
        </template>

        <template v-slot:item.dueSortValue="{ item }">
          <span>{{ item.dueDate }}</span>
        </template>

        <template v-slot:item.note="{ item }">
          <span class="note-html" v-html="item.noteHtml"></span>
        </template>
      </v-data-table>

      <v-card class="px-3 py-1 payments-total-bar" tile dark block color="primary">
        <div class="payments-total-grid">
          <div class="payments-total-row">
            <span class="payments-total-label">Sum</span>
            <span class="payments-total-amount">{{ formatFilteredVsOverall(filteredAmountTotal, overallAmountTotal, formatCurrency) }}</span>
          </div>
        </div>
      </v-card>
    </div>

    <ErrorDialog ref="errorDialog" />
  </div>
</template>

<script>
import ErrorDialog from "../ErrorDialog";
import currency from 'currency.js';
import { ReportsAPIService } from '@/servicehandlers/ReportsAPIService';
import reportTableShared from '@/components/reports/reportTableShared';

const reportsApiService = new ReportsAPIService();
const PAYMENT_REPORT_LABELS = {
  ap: 'Misc. Accts Payable',
  ar: 'Misc. Accts Receivable',
  cap: 'Client Accts Payable',
  car: 'Client Accts Receivable',
  canap: 'Cananwill Accts Payable',
  canar: 'Cananwill Accts Receivable',
  lpp: 'LPP Statement'
};

export default {
  name: 'Payments',
  mixins: [reportTableShared],
  components: { ErrorDialog },
  data() {
    return {
      activeTab: 'cap',
      loading: false,
      hasLoadedAny: false,
      rows: [],
      filter: '',
      reportDate: '',
      currentReportType: 'cap',
      latestRequestId: 0,
      sortBy: ['dueSortValue'],
      sortDesc: [false],
      headers: [
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '90px' },
        { text: 'Payee', value: 'payor', sortable: true, align: 'start', width: '130px' },
        { text: 'Insured', value: 'insured', sortable: true, align: 'start', width: '340px' },
        { text: 'Incept', value: 'inceptSortValue', sortable: true, align: 'start', width: '105px' },
        { text: 'Producer', value: 'producer', sortable: true, align: 'start', width: '105px' },
        { text: 'Type', value: 'type', sortable: true, align: 'start', width: '90px' },
        { text: 'Account', value: 'acct', sortable: true, align: 'start', width: '85px' },
        { text: 'Ref', value: 'ref', sortable: true, align: 'start', width: '110px' },
        { text: 'Note', value: 'note', sortable: true, align: 'start', width: '286px' },
        { text: 'Due', value: 'dueSortValue', sortable: true, align: 'start', width: '105px' },
        { text: 'Amount', value: 'amountValue', sortable: true, align: 'right', width: '100px' }
      ]
    };
  },
  computed: {
    currentReportLabel() {
      return PAYMENT_REPORT_LABELS[this.currentReportType] || 'Payments';
    },

    displayHeaders() {
      const headers = this.headers.map((header) => ({ ...header }));
      const receivableTabs = ['ar', 'car', 'canar'];
      const payorHeader = headers.find((h) => h.value === 'payor');
      if (payorHeader) payorHeader.text = receivableTabs.includes(this.currentReportType) ? 'Payor' : 'Payee';
      return this.showActionsColumn ? headers : headers.filter((h) => h.value !== 'actions');
    },

    overallAmountTotal() {
      return this.rows.reduce((total, row) => total.add(Number(row.amountValue) || 0), currency(0)).value;
    },

    filteredAmountTotal() {
      return this.filteredRows.reduce((total, row) => total.add(Number(row.amountValue) || 0), currency(0)).value;
    }
  },
  created() {
    document.title = 'Payments';
    this.loadPayments('cap');
  },
  methods: {
    async loadPayments(paymentType) {
      const requestId = this.latestRequestId + 1;
      this.latestRequestId = requestId;
      this.activeTab = paymentType;
      this.currentReportType = paymentType;
      this.loading = true;
      this.hasLoadedAny = true;

      try {
        const result = await reportsApiService.getPayment(paymentType, this.$router);
        if (requestId !== this.latestRequestId) return;
        const data = Array.isArray(result) ? result : (result && result.data ? result.data : []);

        this.rows = data.map((item) => {
          const amountValue = Number(item.amt !== undefined ? item.amt : item.amount) || 0;
          const { note, noteHtml } = this.prepareNoteValues(item.note);
          const row = {
            ...item,
            payor: item._mga || item.payor || item._payor || '',
            insured: item.insured || item._insured || item._sort || '',
            incept: this.formatReportDate(item._incept || item.incept || ''),
            inceptSortValue: this.buildDateSortValue(item._incept || item.incept || ''),
            producer: item._producer || item.producer || '',
            type: item.type || item._type || '',
            acct: item.acct || item.account || item._acct || '',
            ref: item.num || item.ref || item._ref || '',
            note,
            noteHtml,
            dueDate: this.formatReportDate(item._due || item.due || ''),
            dueSortValue: this.buildDateSortValue(item._due || item.due || ''),
            amountValue
          };
          return row;
        });
        this.reportDate = this.formatPrintedAt(new Date());
      } catch (e) {
        if (requestId !== this.latestRequestId) return;
        this.rows = [];
        this.$refs.errorDialog.showError({ forDevelopers: e });
      } finally {
        if (requestId !== this.latestRequestId) return;
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
  .payments-summary-bar {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  .payments-table ::v-deep .v-data-table__wrapper {
    /* Fill viewport area; leave room for the single totals row. */
    height: calc(100vh - var(--offset) - 143px) !important;
  }

  .payments-table ::v-deep td,
  .payments-table ::v-deep th {
    padding: 0 6px !important;
    font-size: 12px;
  }

  .payments-table ::v-deep th.text-center span {
    position: relative;
    top: -1px;
  }

  .payments-table.no-actions ::v-deep tr > :first-child {
    padding-left: 12px !important;
  }

  .payments-total-grid {
    width: min(100%, 400px);
    margin-left: auto;
    border-left: 1px solid rgba(255, 255, 255, 0.35);
  }

  .payments-total-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    min-height: 24px;
    font-size: 12px;
    font-weight: 700;
  }

  .payments-total-label {
    padding-left: 14px;
  }

  .payments-total-amount {
    text-align: right;
    font-variant-numeric: tabular-nums;
    border-left: 1px solid rgba(255, 255, 255, 0.28);
    padding-right: 14px;
    white-space: nowrap;
  }

  .payments-table ::v-deep .note-html {
    display: inline;
  }

  .payments-total-bar {
    margin-bottom: 0 !important;
    border-radius: 0 !important;
  }

  @media (max-width: 600px) {
    .payments-summary-text {
      font-size: 11px;
      line-height: 1;
    }
  }
</style>
